/**
 * AuthContext.jsx — provides auth state to the rest of the app.
 * All HTTP calls go through @/services/authService (no axios here).
 */
import { createContext, useContext, useState, useEffect, useCallback, useMemo } from "react";
import { authService } from "@/services";

const AuthContext = createContext(null);

export const useAuth = () => useContext(AuthContext);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    // Skip during a Google OAuth callback — AuthCallback.jsx will load session_id.
    if (window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    try {
      const me = await authService.me();
      setUser(me);
    } catch {
      // 401 when not logged in — expected, no action needed.
      setUser(null);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  // Re-check on tab-becomes-visible. Without this, a user who keeps
  // a tab open across midnight UTC never re-hits /auth/me, so the
  // server-side daily sign-in stamp (record_signin) never records
  // the new day - the streak grid would skip it. Re-checking on
  // visibilitychange is cheap (one GET) and only fires when the
  // user actually engages with the tab.
  //
  // Background-only: a transient failure on the re-check must NOT
  // clear `user` (that would log the user out on a network blip).
  // The initial mount call already handles the genuine-401 path.
  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState !== "visible") return;
      authService.me().then(setUser).catch(() => {/* swallow */});
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => document.removeEventListener("visibilitychange", onVisible);
  }, []);

  const login = (userData) => setUser(userData);

  const logout = async () => {
    try {
      await authService.logout();
    } catch (err) {
      if (process.env.NODE_ENV === "development") {
        console.error("Logout error (non-critical):", err);
      }
    }
    setUser(null);
  };

  const register = async (email, password, name) => {
    const data = await authService.register(email, password, name);
    setUser(data);
    return data;
  };

  const loginWithEmail = async (email, password) => {
    const data = await authService.login(email, password);
    // 2FA short-circuit (KNOWN_ISSUES §1.49). When the server returns
    // `requires_2fa: true`, password was correct but a second factor
    // is needed — caller renders the challenge form. Don't set the
    // user here; the session cookie isn't created until /2fa/verify.
    if (data?.requires_2fa) return data;
    setUser(data);
    return data;
  };

  const loginWithTwoFA = async (challengeToken, opts) => {
    const data = await authService.twoFAVerify(challengeToken, opts);
    setUser(data);
    return data;
  };

  const loginWithGoogle = async (credential) => {
    // GIS ID-token flow (KNOWN_ISSUES §1.54) — the GoogleLogin button
    // hands us an ID token; backend verifies it and sets the cookie.
    const data = await authService.googleAuth(credential);
    setUser(data);
    return data;
  };

  const contextValue = useMemo(
    () => ({ user, loading, login, logout, register, loginWithEmail, loginWithTwoFA, loginWithGoogle, checkAuth }),
    [user, loading, checkAuth],
  );

  return <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>;
}
